/**
 * شاشة التسجيل الذاتي — نفس تخطيط الدخول بلوحين
 * (docs/09-التسجيل-الذاتي-والاعتماد.md · «شاشة جديدة: تسجيل»).
 *
 * القاعدة اللي بتحكم الشاشة دي: **القسم اللي بيكتبه الموظف استرشادي بس**.
 * الإدارة هي اللي بتحدد القسم والدور وقت الاعتماد — عشان كده فيه سطر تحت
 * القايمة بيقول كده بالحرف، ومفيش قايمة أدوار أصلًا.
 *
 * ⚠️ الرد من السيستم **202 دايمًا** حتى لو البريد مسجّل قبل كده (صمود ضد
 *    التعداد). يعني «تم» هنا معناها «طلبك وصل» — مش «الحساب اتعمل».
 *
 * ⛔ مفيش زر «اسأل المساعد» في شاشات المصادقة.
 */
import { useState, type CSSProperties, type FormEvent } from "react";
import { Icon } from "@/components/Icon";
import { PasswordReveal } from "@/components/PasswordReveal";
import { ApiError, OfflineError, errorMessageAr } from "@/api/client";
import { register } from "@/api/auth";
import { Link, LOGIN_PATH } from "@/lib/router";
import { Num } from "@/lib/format";
import { AuthPitch } from "./Login";
import "@/styles/auth.css";

/** الأقسام التمانية المعتمدة — القيم من `Department` في الباك إند */
const DEPARTMENTS: { value: string; label: string }[] = [
  { value: "student_support", label: "دعم الطلاب (المبيعات)" },
  { value: "accounts", label: "الحسابات" },
  { value: "admin_supervision", label: "الإشراف الإداري" },
  { value: "evaluation", label: "التقييم والترشيح" },
  { value: "hiring", label: "التوظيف" },
  { value: "training_recordings", label: "التدريب والتسجيلات" },
  { value: "technical_supervision", label: "الإشراف الفني" },
  { value: "management", label: "الإدارة" },
];

/**
 * أقل طول لكلمة السر — **لازم يساوي `password_min_length` في الباك إند (٨)**.
 * 🔴 كان ٨ هنا و١٠ هناك: الشاشة بتقول «٨ محارف» والموظفة تكتب ٩، السيرفر
 *    يرفض، والرفض كان بيتعدّ على حد التسجيل لحد ما يقفلها ساعة.
 */
const MIN_PASSWORD = 8;

type FieldKey = "full_name_ar" | "email" | "password" | "claimed_department";

export function Register() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [department, setDepartment] = useState("");

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [badField, setBadField] = useState<FieldKey | null>(null);

  function fail(field: FieldKey | null, message: string) {
    setBadField(field);
    setError(message);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // قفل ضد الدوس المزدوج — تسجيلين لنفس الشخص بيعملوا طلبين للمدير
    if (busy || done) return;

    const name = fullName.trim();
    const mail = email.trim();

    if (name.length < 3) return fail("full_name_ar", "اكتب اسمك الكامل زي ما هو في الأكاديمية");
    if (!mail || !mail.includes("@")) return fail("email", "اكتب بريد صحيح — هيوصله رابط التأكيد");
    // ⚠️ الرقم مكتوب بالحروف هنا عن قصد: الأرقام اللاتينية لازم تكون جوّه
    //    `<span className="num">`، ورسالة الخطأ نص مجرّد مش JSX. الرقم نفسه
    //    ظاهر صح تحت الحقل في `<Num v={MIN_PASSWORD} />`.
    if (password.length < MIN_PASSWORD) {
      return fail("password", `كلمة السر لازم تكون ${MIN_PASSWORD} محارف على الأقل`);
    }
    if (!department) return fail("claimed_department", "اختار القسم اللي بتشتغل فيه");

    setBusy(true);
    setError(null);
    setBadField(null);
    try {
      await register({
        full_name_ar: name,
        email: mail,
        password,
        claimed_department: department,
      });
      // ⚠️ مش بنقول «الحساب اتعمل» — السيستم بيرد 202 على أي بريد.
      //    اللي اتم فعلًا هو إن الطلب وصل.
      setPassword("");
      setDone(true);
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت — الرسالة بتبان للموظف والسبب بيتسجّل
      const serverField = err instanceof ApiError ? err.field : null;
      setBadField(isFieldKey(serverField) ? serverField : null);
      if (err instanceof ApiError && err.status === 404) {
        // ⚠️ ٤٠٤ هنا معناه إن السيستم لسه ما فتحش التسجيل الذاتي — مش إن
        //    الموظف كتب حاجة غلط. النص الاحتياطي «مالقيناش اللي بتدوّر عليه»
        //    كان هيخلّيه يفضل يعيد المحاولة على حاجة مش هتشتغل.
        console.error("[register] مسار التسجيل الذاتي مش موجود في السيستم", err);
        setError("التسجيل الذاتي لسه مش مفتوح — برجاء التواصل مع إدارة الأكاديمية");
      } else {
        setError(errorMessageAr(err));
      }
      if (!(err instanceof ApiError) && !(err instanceof OfflineError)) {
        console.error("[register] فشل مش متوقع", err);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth" dir="rtl">
      {/* ================= (أ) اليمين — النموذج ================= */}
      <section className="auth-pane form">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="brand-mark">
              <Icon name="book" size={20} stroke="#0F1D33" />
            </div>
            <div>
              <div className="brand-name">رتّل</div>
              <div className="brand-sub">السيستم الإداري</div>
            </div>
          </div>

          {done ? (
            <DonePanel email={email.trim()} />
          ) : (
            <form onSubmit={onSubmit} noValidate className="auth-form">
              <div style={{ marginTop: 32 }}>
                <div className="auth-title">تسجيل حساب</div>
                <div className="t-desc" style={{ marginTop: 8 }}>
                  سجّل ببريدك، والإدارة بتراجع طلبك وتفعّله
                </div>
              </div>

              {error ? (
                <div className="err-box" style={{ marginTop: 20 }} role="alert" aria-live="assertive">
                  <Icon name="alert" size={18} stroke="#B42318" />
                  <span className="t-body" style={{ color: "var(--danger)" }}>
                    {error}
                  </span>
                </div>
              ) : null}

              <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                {/* الاسم — عربي، فممنوع dir="ltr" هنا */}
                <div>
                  <label className="field-label" htmlFor="full-name">
                    الاسم الكامل
                  </label>
                  <div className={inputCls(badField === "full_name_ar")} style={{ marginTop: 8 }}>
                    <Icon name="team" size={16} stroke="#98A2B3" />
                    <input
                      id="full-name"
                      name="full_name_ar"
                      type="text"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="reg-email">
                    البريد
                  </label>
                  {/* dir="ltr" مسموح: بريد لاتيني صافي */}
                  <div className={inputCls(badField === "email")} style={{ marginTop: 8 }}>
                    <Icon name="mail" size={16} stroke="#98A2B3" />
                    <input
                      id="reg-email"
                      name="email"
                      type="email"
                      dir="ltr"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="reg-password">
                    كلمة السر
                  </label>
                  <div className={inputCls(badField === "password")} style={{ marginTop: 8 }}>
                    <Icon name="lock" size={16} stroke="#98A2B3" />
                    <input
                      id="reg-password"
                      name="password"
                      type={showPw ? "text" : "password"}
                      dir="ltr"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={busy}
                    />
                    <PasswordReveal shown={showPw} onToggle={() => setShowPw((v) => !v)} disabled={busy} />
                  </div>
                  <div className="t-meta" style={{ marginTop: 6 }}>
                    <Num v={MIN_PASSWORD} /> حروف أو أرقام على الأقل
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="reg-department">
                    القسم
                  </label>
                  <div className={inputCls(badField === "claimed_department")} style={{ marginTop: 8 }}>
                    <Icon name="access" size={16} stroke="#98A2B3" />
                    <select
                      id="reg-department"
                      name="claimed_department"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      disabled={busy}
                      style={SELECT_STYLE}
                    >
                      <option value="">اختار قسمك</option>
                      {DEPARTMENTS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <span style={{ display: "flex", transform: "rotate(90deg)" }}>
                      <Icon name="chevron" size={14} stroke="#98A2B3" />
                    </span>
                  </div>
                  {/* ⚠️ السطر ده مش زينة: بيمنع الموظف يفتكر إن اختياره بيدّيه صلاحية */}
                  <div className="t-meta" style={{ marginTop: 6 }}>
                    الإدارة هي اللي بتحدد قسمك ودورك النهائي
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className={`btn primary${busy ? " is-busy" : ""}`}
                style={{ marginTop: 20, height: 48 }}
                disabled={busy}
              >
                {busy ? (
                  <>
                    <Icon name="spinner" size={16} stroke="#FFFFFF" strokeWidth={1.9} />
                    بيسجّل…
                  </>
                ) : (
                  "تسجيل"
                )}
              </button>

              <div className="t-body" style={{ marginTop: 16, textAlign: "center", fontWeight: 400 }}>
                عندك حساب —{" "}
                <Link to={LOGIN_PATH} className="link">
                  ادخل
                </Link>
              </div>

              <div className="t-meta" style={{ marginTop: 10, textAlign: "center" }}>
                الحساب ما بيشتغلش قبل ما الإدارة تعتمده
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ================= (ب) اليسار — التعريف ================= */}
      <AuthPitch />
    </div>
  );
}

/* ───────────── بعد الإرسال ─────────────
   النص متعمّد إنه مشروط («لو البريد صحيح») — الرد 202 دايمًا. */

function DonePanel({ email }: { email: string }) {
  return (
    <div className="auth-form">
      <div style={{ marginTop: 32 }}>
        <div className="auth-title">طلبك وصل</div>
      </div>

      <div
        className="err-box"
        style={{ marginTop: 20, background: "var(--ok-bg)", borderColor: "#BBF7D0", alignItems: "flex-start" }}
        role="status"
        aria-live="polite"
      >
        <Icon name="check" size={18} stroke="#067647" strokeWidth={2.2} />
        <span className="t-body" style={{ color: "var(--ok)", fontWeight: 400 }}>
          لو البريد صحيح هيوصلك رابط تأكيد. افتح الرابط، وبعدها الإدارة بتراجع طلبك
          وتفعّل الحساب.
        </span>
      </div>

      {email ? (
        <div className="t-meta" style={{ marginTop: 12 }}>
          البريد اللي كتبته: <span dir="ltr">{email}</span>
        </div>
      ) : null}

      <div className="t-body" style={{ marginTop: 20, textAlign: "center", fontWeight: 400 }}>
        <Link to={LOGIN_PATH} className="link">
          رجوع لتسجيل الدخول
        </Link>
      </div>

      <div className="t-meta" style={{ marginTop: 10, textAlign: "center" }}>
        ما وصلكش الرابط؟ برجاء التواصل مع إدارة الأكاديمية
      </div>
    </div>
  );
}

/* ───────────── مساعدات ───────────── */

function inputCls(invalid: boolean): string {
  return invalid ? "input invalid" : "input";
}

function isFieldKey(v: string | null): v is FieldKey {
  return (
    v === "full_name_ar" || v === "email" || v === "password" || v === "claimed_department"
  );
}

/**
 * الـ`select` بياخد نفس شكل الـ`input` جوّه `.input` — بلا حد ولا خلفية،
 * والسهم بتاعنا بدل سهم المتصفح عشان الشكل يفضل واحد في كل المتصفحات.
 */
const SELECT_STYLE: CSSProperties = {
  border: 0,
  outline: "none",
  background: "none",
  font: "inherit",
  color: "var(--ink)",
  width: "100%",
  minWidth: 0,
  padding: 0,
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
};
